import React, { useRef, useState, useEffect, useCallback } from 'react';
import { X, Check, Loader2, ZoomIn } from 'lucide-react';

/**
 * Telegram-style square crop + zoom for profile pictures.
 *
 * The user drags to pan and uses the slider to zoom; the visible square is
 * exported as a normalized 512×512 JPEG. Because every template receives the
 * same pre-cropped square (with the user's chosen focal area centered), the
 * head / important part never gets cut off regardless of how a given template
 * frames the realtor image.
 *
 * Dependency-free: HTML canvas for the export, pointer events for the drag,
 * a native range input for zoom. Returns a Blob via onConfirm(blob).
 */
const VIEWPORT = 280; // on-screen crop square (px)
const OUTPUT = 512;   // exported square (px)
const MAX_ZOOM = 4;   // relative to the cover-fit minimum

export default function ImageCropModal({ file, onCancel, onConfirm }) {
  const [imgUrl, setImgUrl] = useState(null);
  const imgRef = useRef(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // image top-left within viewport (px)
  const dragRef = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const clamp = useCallback((off, s, nat) => {
    const dw = nat.w * s;
    const dh = nat.h * s;
    const x = Math.min(0, Math.max(VIEWPORT - dw, off.x));
    const y = Math.min(0, Math.max(VIEWPORT - dh, off.y));
    return { x, y };
  }, []);

  const onImgLoad = (e) => {
    const w = e.target.naturalWidth;
    const h = e.target.naturalHeight;
    const nat = { w, h };
    setNatural(nat);
    const ms = Math.max(VIEWPORT / w, VIEWPORT / h); // cover the viewport
    setMinScale(ms);
    setScale(ms);
    setOffset({ x: (VIEWPORT - w * ms) / 2, y: (VIEWPORT - h * ms) / 2 });
  };

  const onPointerDown = (e) => {
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: offset.x, oy: offset.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const nx = dragRef.current.ox + (e.clientX - dragRef.current.sx);
    const ny = dragRef.current.oy + (e.clientY - dragRef.current.sy);
    setOffset(clamp({ x: nx, y: ny }, scale, natural));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const onZoom = (newScale) => {
    // Keep the viewport center anchored while zooming.
    const cx = VIEWPORT / 2;
    const cy = VIEWPORT / 2;
    const ix = (cx - offset.x) / scale;
    const iy = (cy - offset.y) / scale;
    const ns = newScale;
    const moved = { x: cx - ix * ns, y: cy - iy * ns };
    setScale(ns);
    setOffset(clamp(moved, ns, natural));
  };

  const handleConfirm = async () => {
    if (!imgRef.current) return;
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT;
      canvas.height = OUTPUT;
      const ctx = canvas.getContext('2d');
      // White backdrop so transparent PNGs don't export black corners.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, OUTPUT, OUTPUT);
      // Source square (in natural-image coords) currently visible in the viewport.
      const sx = -offset.x / scale;
      const sy = -offset.y / scale;
      const sSize = VIEWPORT / scale;
      ctx.drawImage(imgRef.current, sx, sy, sSize, sSize, 0, 0, OUTPUT, OUTPUT);
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.92));
      if (blob) onConfirm(blob);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl bg-[#fffdf7] border border-[#d8d0bd] p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-[#23211a]">Bild zuschneiden</h3>
          <button type="button" onClick={onCancel} className="p-1.5 rounded hover:bg-[#efe9db] text-[#6f6a5c] hover:text-[#23211a]" aria-label="Schließen">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-[#6f6a5c] mb-3">
          Ziehen zum Positionieren, Regler zum Zoomen. Der gesamte Ausschnitt wird verwendet — halte dein Gesicht im Kreis, dann passt es auch in runden Vorlagen.
        </p>

        {/* Crop viewport */}
        <div className="flex justify-center">
          <div
            className="relative overflow-hidden rounded-lg bg-[#f7f3ea] touch-none select-none cursor-grab active:cursor-grabbing"
            style={{ width: VIEWPORT, height: VIEWPORT }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {imgUrl && (
              <img
                ref={imgRef}
                src={imgUrl}
                alt=""
                onLoad={onImgLoad}
                draggable={false}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: natural.w,
                  height: natural.h,
                  transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                  transformOrigin: 'top left',
                  maxWidth: 'none',
                }}
              />
            )}
            {/* The whole square is captured. The ring marks the "safe zone"
                for templates that show the photo inside a circle — we don't
                dim the corners because they ARE kept for square frames. */}
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute inset-0 rounded-full border border-dashed border-white/50" />
            </div>
          </div>
        </div>

        {/* Zoom slider */}
        <div className="flex items-center gap-3 mt-4">
          <ZoomIn className="w-4 h-4 text-[#6f6a5c] flex-shrink-0" />
          <input
            type="range"
            min={minScale}
            max={minScale * MAX_ZOOM}
            step={(minScale * (MAX_ZOOM - 1)) / 100 || 0.01}
            value={scale}
            onChange={(e) => onZoom(parseFloat(e.target.value))}
            className="flex-1 accent-[#1f4d3a]"
            aria-label="Zoom"
          />
        </div>

        <div className="flex gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 h-10 rounded-md border border-[#d8d0bd] text-[#4a463b] hover:bg-[#efe9db] text-sm font-medium"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !imgUrl}
            data-testid="crop-confirm"
            className="flex-1 h-10 rounded-md bg-[#1f4d3a] hover:bg-[#163a2c] text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Übernehmen
          </button>
        </div>
      </div>
    </div>
  );
}
